import { useEffect, useState } from "react";
import { ExternalLink, CheckCircle2, XCircle, Coins, ArrowDownToLine, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const statusChip = (s) => {
  const cls =
    s === "pending" ? "border-gold/40 bg-gold/10 text-gold" : s === "approved" || s === "paid"
      ? "border-profit/40 bg-profit/10 text-profit" : "border-loss/40 bg-loss/10 text-loss";
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 font-mono text-[9px] font-600 uppercase tracking-[0.12em] ${cls}`}>{s}</span>
  );
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

export default function CoinsTab() {
  const [claims, setClaims] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [coinsFor, setCoinsFor] = useState({});
  const token = localStorage.getItem("te_token");

  const load = () => {
    api.get("/admin/coin-claims").then(({ data }) => setClaims(data)).catch(() => {});
    api.get("/admin/withdrawals").then(({ data }) => setWithdrawals(data)).catch(() => {});
    api.get("/admin/redemptions").then(({ data }) => setRedemptions(data)).catch(() => {});
  };
  useEffect(load, []);

  const approve = async (id) => {
    const coins = parseFloat(coinsFor[id] || "");
    if (!coins || coins <= 0) {
      toast.error("ENTER COINS TO CREDIT");
      return;
    }
    try {
      await api.post(`/admin/coin-claims/${id}/approve`, { coins });
      toast.success(`CREDITED ${coins} COINS`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const rejectClaim = async (id) => {
    const note = window.prompt("Reason shown to the member (optional):") ?? "";
    try {
      await api.post(`/admin/coin-claims/${id}/reject`, { note });
      toast.success("CLAIM REJECTED");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const markPaid = async (id) => {
    try {
      await api.post(`/admin/withdrawals/${id}/mark-paid`);
      toast.success("MARKED PAID");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const rejectWithdrawal = async (id) => {
    const note = window.prompt("Reason shown to the member (optional):") ?? "";
    try {
      await api.post(`/admin/withdrawals/${id}/reject`, { note });
      toast.success("REJECTED — COINS REFUNDED");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const approveRedemption = async (id) => {
    const note = window.prompt("Delivery note for the member (optional):") ?? "";
    try {
      await api.post(`/admin/redemptions/${id}/approve`, { note });
      toast.success("REDEMPTION APPROVED");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const rejectRedemption = async (id) => {
    const note = window.prompt("Reason shown to the member (optional):") ?? "";
    try {
      await api.post(`/admin/redemptions/${id}/reject`, { note });
      toast.success("REJECTED — REFERRAL COINS REFUNDED");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const pendingClaims = claims.filter((c) => c.status === "pending");
  const pendingWds = withdrawals.filter((w) => w.status === "pending");
  const pendingRds = redemptions.filter((r) => r.status === "pending");

  return (
    <div data-testid="coins-tab">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="card-violet p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Pending Claims</p>
          <p className="mt-2 font-display text-3xl font-800 text-gold" data-testid="pending-claims-count">{pendingClaims.length}</p>
        </div>
        <div className="card-violet p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Pending Withdrawals</p>
          <p className="mt-2 font-display text-3xl font-800 text-signal" data-testid="pending-withdrawals-count">{pendingWds.length}</p>
        </div>
        <div className="card-violet p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Pending Redemptions</p>
          <p className="mt-2 font-display text-3xl font-800 text-profit" data-testid="pending-redemptions-count">{pendingRds.length}</p>
        </div>
      </div>

      <div className="card-violet mt-6 p-6 sm:p-7">
        <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-signal">
          <ShoppingBag size={13} /> Account Redemptions (referral coins)
        </p>
        <div className="mt-4 space-y-3" data-testid="admin-redemptions-list">
          {redemptions.length === 0 && <p className="font-mono text-xs text-muted">No redemption requests yet.</p>}
          {redemptions.map((r) => (
            <div key={r.id} className="rounded-xl border border-line bg-ink p-4" data-testid={`admin-redemption-${r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-600 text-white">{r.firm_name} · {r.account_size} · {r.amount_coins} coins (${r.amount_coins})</p>
                  <p className="mt-0.5 font-mono text-[10px] text-muted">{r.user_name} ({r.user_email}) · {fmtDate(r.created_at)}</p>
                  {r.admin_note && <p className="mt-1 font-mono text-[10px] text-muted">Note: {r.admin_note}</p>}
                </div>
                {statusChip(r.status)}
              </div>
              {r.status === "pending" && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button onClick={() => approveRedemption(r.id)} data-testid={`redemption-approve-${r.id}`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-profit/50 bg-profit/10 px-4 py-2 font-mono text-[10px] font-700 uppercase tracking-[0.12em] text-profit transition-colors hover:bg-profit/20">
                    <CheckCircle2 size={12} /> Approve + Deliver
                  </button>
                  <button onClick={() => rejectRedemption(r.id)} data-testid={`redemption-reject-${r.id}`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-loss/40 bg-loss/10 px-4 py-2 font-mono text-[10px] font-700 uppercase tracking-[0.12em] text-loss transition-colors hover:bg-loss/20">
                    <XCircle size={12} /> Reject + Refund
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="card-violet mt-6 p-6 sm:p-7">
        <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-signal">
          <Coins size={13} /> Purchase Claims
        </p>
        <div className="mt-4 space-y-3" data-testid="admin-claims-list">
          {claims.length === 0 && <p className="font-mono text-xs text-muted">No claims yet.</p>}
          {claims.map((c) => (
            <div key={c.id} className="rounded-xl border border-line bg-ink p-4" data-testid={`admin-claim-${c.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-600 text-white">{c.firm_name} · {c.account_size} {c.order_ref ? `· ${c.order_ref}` : ""}</p>
                  <p className="mt-0.5 font-mono text-[10px] text-muted">{c.user_name} ({c.user_email}) · claimed {fmtDate(c.created_at)}{c.purchase_date ? ` · bought ${c.purchase_date}` : ""}</p>
                </div>
                <div className="flex items-center gap-2">
                  {statusChip(c.status)}
                  <a href={`${api.defaults.baseURL}/admin/coin-claims/${c.id}/image?auth=${encodeURIComponent(token || "")}`}
                    target="_blank" rel="noopener noreferrer" data-testid={`claim-image-${c.id}`}
                    className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.12em] text-signal hover:text-white">
                    Screenshot <ExternalLink size={10} />
                  </a>
                </div>
              </div>
              {c.status === "pending" && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <input
                    value={coinsFor[c.id] || ""}
                    onChange={(e) => setCoinsFor({ ...coinsFor, [c.id]: e.target.value.replace(/[^0-9.]/g, "") })}
                    placeholder="Coins"
                    inputMode="decimal"
                    data-testid={`claim-coins-input-${c.id}`}
                    className="w-28 rounded-lg border border-line bg-surface px-3 py-2 font-mono text-xs text-white outline-none focus:border-profit"
                  />
                  <button onClick={() => approve(c.id)} data-testid={`claim-approve-${c.id}`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-profit/50 bg-profit/10 px-4 py-2 font-mono text-[10px] font-700 uppercase tracking-[0.12em] text-profit transition-colors hover:bg-profit/20">
                    <CheckCircle2 size={12} /> Approve + Credit
                  </button>
                  <button onClick={() => rejectClaim(c.id)} data-testid={`claim-reject-${c.id}`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-loss/40 bg-loss/10 px-4 py-2 font-mono text-[10px] font-700 uppercase tracking-[0.12em] text-loss transition-colors hover:bg-loss/20">
                    <XCircle size={12} /> Reject
                  </button>
                </div>
              )}
              {c.status === "approved" && <p className="mt-2 font-mono text-[10px] font-700 text-gold">+{c.coins} coins credited</p>}
            </div>
          ))}
        </div>
      </div>

      <div className="card-violet mt-6 p-6 sm:p-7">
        <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-signal">
          <ArrowDownToLine size={13} /> Withdrawal Requests
        </p>
        <div className="mt-4 space-y-3" data-testid="admin-withdrawals-list">
          {withdrawals.length === 0 && <p className="font-mono text-xs text-muted">No withdrawals yet.</p>}
          {withdrawals.map((w) => (
            <div key={w.id} className="rounded-xl border border-line bg-ink p-4" data-testid={`admin-withdrawal-${w.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-600 text-white">{w.amount_coins} coins (${w.amount_coins}) · {w.method.toUpperCase()}</p>
                  <p className="mt-0.5 font-mono text-[10px] text-muted">{w.user_name} ({w.user_email}) · {fmtDate(w.created_at)}</p>
                  <p className="mt-1 break-all font-mono text-[11px] font-600 text-signal" data-testid={`payout-detail-${w.id}`}>{w.payout_detail}</p>
                </div>
                {statusChip(w.status)}
              </div>
              {w.status === "pending" && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button onClick={() => markPaid(w.id)} data-testid={`withdrawal-paid-${w.id}`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-profit/50 bg-profit/10 px-4 py-2 font-mono text-[10px] font-700 uppercase tracking-[0.12em] text-profit transition-colors hover:bg-profit/20">
                    <CheckCircle2 size={12} /> Mark Paid
                  </button>
                  <button onClick={() => rejectWithdrawal(w.id)} data-testid={`withdrawal-reject-${w.id}`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-loss/40 bg-loss/10 px-4 py-2 font-mono text-[10px] font-700 uppercase tracking-[0.12em] text-loss transition-colors hover:bg-loss/20">
                    <XCircle size={12} /> Reject + Refund
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
